import { DecimalPipe, NgClass, NgIf } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	HostBinding,
	Input,
	OnChanges,
	Optional,
	Output,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InputComponent } from '@woolworthsnz/form';
import {
	ButtonComponent,
	ButtonGroupComponent,
	FeatureService,
	LocalStorageService,
	ModalOverlayService,
	NumericDirective,
	ShopperService,
	StorageKeys,
	SvgIconComponent,
} from '@woolworthsnz/styleguide';
import { ContextResponse, ProductResponse, QuantityResponse } from '@woolworthsnz/trader-api';
import { ProductService } from '../../services';
import { LayoutType } from '../../models/layout-type.model';

export interface IncrementRoundingParams {
	val: number;
	min: number;
	increment: number;
}

@Component({
	selector: 'product-quantity-selector',
	templateUrl: './quantity-selector.component.html',
	styleUrls: ['./quantity-selector.component.scss'],
	providers: [DecimalPipe],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		ButtonGroupComponent,
		FormsModule,
		ReactiveFormsModule,
		InputComponent,
		NumericDirective,
		NgClass,
		NgIf,
		ButtonComponent,
		SvgIconComponent,
	],
})
export class QuantitySelectorComponent implements OnChanges {
	@Input() quantityResponse: QuantityResponse;

	@Input() pricePerKG: number;
	@Input() unit: ProductResponse.UnitEnum = ProductResponse.UnitEnum.Kg;
	@Input() averageWeightPerUnit: number | undefined;
	@Input() disableKeyboardInput = false;
	@Input() customLabel: string | undefined;

	@HostBinding('attr.data-layout')
	@Input()
	layout: LayoutType = LayoutType.grid;

	@Output() incrementEmitter: EventEmitter<number> = new EventEmitter();
	@Output() decrementEmitter: EventEmitter<number> = new EventEmitter();
	@Output() updateEmitter: EventEmitter<number> = new EventEmitter();

	@ViewChild('decrementButton', { static: true }) decrementButton: ElementRef;
	@ViewChild('incrementButton', { static: true }) incrementButton: ElementRef;
	@ViewChild('quantityInput', { static: true }) quantityInput: InputComponent;
	@ViewChild('weight', { static: true }) weightDisplay: ElementRef;
	@ViewChild('notification', { static: true }) notification: ElementRef;

	addedText = 'added';
	decrementLabel = 'Decrease quantity';
	defaultMaxQuantity = 100;
	incrementLabel = 'Increase quantity';
	inputHasFocus = false;
	inTrolleyText = '&nbsp;in trolley';
	name: string;
	quantityForm: FormGroup;

	constructor(
		private decimalPipe: DecimalPipe,
		private fb: FormBuilder,
		private featureService: FeatureService,
		private shopperService: ShopperService,
		private localStorageService: LocalStorageService,
		private modalOverlayService: ModalOverlayService,
		@Optional() private productService: ProductService
	) {
		this.quantityForm = this.fb.group({
			quantity: 0,
		});
	}

	get alertText(): string {
		// Avoids a glitch in animation when decrementing down
		// Will most likely pop up as a bug when someone has a max and min as 1
		return !this.canIncrement || Number(this.roundedDecimalValue) >= this.maximumQuantity - this.incrementQuantity
			? 'Order limit reached'
			: 'Minimum Order';
	}

	get buttonIconSize(): string {
		return 'small';
	}

	get canIncrement(): boolean {
		return Number(this.roundedDecimalValue) < this.maximumQuantity;
	}

	get canDecrement(): boolean {
		return Number(this.roundedDecimalValue) >= this.minimumQuantity;
	}

	get currentValue(): number {
		return Number(this.quantityForm.get('quantity')?.value);
	}

	get estimatedPrice(): string {
		return this.productService?.getPriceAsCurrency(
			this.decimalPipe.transform(Number(this.roundedDecimalValue) * this.pricePerKG) || 0
		);
	}

	get incrementQuantity(): number {
		// Unit can be modified if it is a kg item where the user has selected 'each'
		// so we force the increment to 1
		if (this.unit === ProductResponse.UnitEnum.Each) {
			return 1;
		}

		return this.quantityResponse?.increment || 0.1;
	}

	get maximumQuantity(): number {
		let maximumQuantity = this.quantityResponse?.max || this.defaultMaxQuantity;

		if (this.unit === ProductResponse.UnitEnum.Each) {
			maximumQuantity = this.productService?.getEachQuantityForGivenWeight(
				maximumQuantity,
				this.averageWeightPerUnit
			);
		}

		return maximumQuantity;
	}

	get maximumAmountSelected(): boolean {
		return this.roundedDecimalValue === this.maximumQuantity;
	}

	get minimumQuantity(): number {
		if (this.unit === ProductResponse.UnitEnum.Each) {
			return 1;
		}

		return this.quantityResponse?.min || 0.1;
	}

	get minimumAmountSelected(): boolean {
		return this.roundedDecimalValue === this.minimumQuantity;
	}

	get quantityLabel(): string {
		return `Enter desired quantity${this.unit === ProductResponse.UnitEnum.Kg ? ' in kilograms' : ''}:`;
	}

	get roundedDecimalValue(): number | string {
		const decimalValue = this.decimalPipe.transform(this.currentValue, '1.2-2');
		const useval = decimalValue === null ? this.currentValue : decimalValue;
		return this.unit === ProductResponse.UnitEnum.Kg ? useval : this.currentValue;
	}

	get showDoneButton(): boolean {
		return !this.disableKeyboardInput && !!(this.inputHasFocus || this.quantityForm.get('quantity')?.touched);
	}

	get inputMode(): string {
		return this.unit === ProductResponse.UnitEnum.Kg ? 'decimal' : 'numeric';
	}

	@Input() set id(val: number | string) {
		this.name = `quantity-${val}`;
	}

	set currentValue(val: number) {
		const quantity = Number.isNaN(val) ? 0 : parseFloat(this.decimalPipe.transform(val, '1.2-2') || '0');

		this.quantityForm.patchValue({
			quantity,
		});
	}

	ngOnChanges(c: SimpleChanges): void {
		if (c.quantityResponse) {
			const currentValue = c.quantityResponse.currentValue.value;
			this.currentValue = currentValue;

			if (this.unit === ProductResponse.UnitEnum.Each) {
				this.currentValue = this.productService?.getEachQuantityForGivenWeight(
					currentValue,
					this.averageWeightPerUnit
				);
			}
		}
	}

	constrainQuantity(val = this.roundedDecimalValue, decrementing = false): number {
		// eslint-disable-next-line eqeqeq
		if (val === 0) {
			return 0;
		}

		if (Number(val) < this.minimumQuantity && !decrementing) {
			return this.minimumQuantity;
		} else if (Number(val) < this.minimumQuantity && decrementing) {
			return 0;
		}

		if (this.unit === ProductResponse.UnitEnum.Kg) {
			return this.roundToIncrement({
				val: Number(val),
				min: this.quantityResponse.min || 1,
				increment: this.quantityResponse.increment || 1,
			});
		}

		return Number(val);
	}

	decrement(): void {
		// TODO: If the quantity selector needs to live in a modal again,
		// we need to revisit resetting the value to 0

		if (this.shouldShowLiquorLegislationModal()) {
			this.openLiquorLegislationModal(false);
		} else {
			this.currentValue = this.currentValue - this.incrementQuantity;

			this.currentValue = this.currentValue && this.constrainQuantity(this.roundedDecimalValue, true);

			this.decrementEmitter.emit(this.currentValue);
		}
	}

	increment(): void {
		if (this.shouldShowLiquorLegislationModal()) {
			this.openLiquorLegislationModal(true);
		} else {
			if (!this.canIncrement) {
				return;
			}
			this.currentValue = this.currentValue + this.incrementQuantity;
			this.currentValue = this.currentValue && this.constrainQuantity(this.roundedDecimalValue);
			this.incrementEmitter.emit(this.currentValue);
		}
	}

	inputKeyup(event: { keyCode: number }): void {
		if (event instanceof KeyboardEvent && event.key === 'Enter') {
			this.quantityInput.input.nativeElement.blur();
			this.update();
		}
	}

	roundToIncrement = ({ val, min, increment }: IncrementRoundingParams): number => {
		if (parseFloat(((val - min) * (1 / increment)).toFixed(2)) % 1 === 0) {
			return val;
		}

		return parseFloat((Math.ceil((val - min) * (1 / increment)) / (1 / increment) + min).toFixed(2));
	};

	update(): void {
		if (isNaN(parseFloat(String(this.currentValue)))) {
			this.currentValue = 0;
		}

		this.currentValue = this.constrainQuantity(this.roundedDecimalValue);
		this.updateEmitter.emit(Number(this.roundedDecimalValue));

		this.quantityForm.get('quantity')?.markAsPristine();
		this.quantityForm.get('quantity')?.markAsUntouched();
	}

	shouldShowLiquorLegislationModal(): boolean {
		const IsAgeRestrictedPopupEnabled = this.featureService.isFeatureEnabled(
			ContextResponse.EnabledFeaturesEnum.IsAgeRestrictedPopupEnabled
		);
		const isAgeRestrictedItem = this.productService?.product?.isAgeRestricted || false;
		const cacheAgeConfirmed = this.localStorageService.getItem(StorageKeys.ageRestrictedProductAcknowledged);
		const ageConfirmed = this.shopperService.state?.shopperScvId
			? cacheAgeConfirmed === this.shopperService.state?.shopperScvId
			: cacheAgeConfirmed === 'confirmed';
		return IsAgeRestrictedPopupEnabled && isAgeRestrictedItem && !ageConfirmed;
	}

	openLiquorLegislationModal(isIncrement = true): void {
		const confirmAction = (): void => {
			const scvid = this.shopperService.state?.shopperScvId || 'confirmed';
			this.localStorageService.setItem(StorageKeys.ageRestrictedProductAcknowledged, scvid);
			if (isIncrement) {
				this.increment();
			} else {
				this.decrement();
			}
		};
		this.modalOverlayService.openLiquorLegislationModal(confirmAction);
	}
}
